import { useState, useEffect } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/api';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';
import { User, Bell, Shield, Mail } from 'lucide-react';

export const Route = createFileRoute('/_auth/account')({ component: AccountPage });

export default function AccountPage() {
  const qc = useQueryClient();

  const { data: me, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: () => adminApi.auth.me(),
  });

  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [receiveAlerts, setReceiveAlerts] = useState(false);

  // Sync form when data arrives
  useEffect(() => {
    if (!me) return;
    const m = me as { firstName?: string; lastName?: string; name?: string; receiveAlerts?: boolean };
    setFirstName(m.firstName ?? m.name?.split(' ')[0] ?? '');
    setLastName(m.lastName  ?? m.name?.split(' ').slice(1).join(' ') ?? '');
    setReceiveAlerts(m.receiveAlerts ?? false);
  }, [me]);

  const { mutate: save, isPending } = useMutation({
    mutationFn: () => adminApi.auth.updateMe({ firstName, lastName, receiveAlerts }),
    onSuccess: () => {
      toast.success('Profile updated');
      qc.invalidateQueries({ queryKey: ['me'] });
    },
    onError: (e: Error) => toast.error(e?.message ?? 'Failed to save'),
  });

  if (isLoading) return (
    <div style={{ padding: '60px 0', textAlign: 'center' }}><Spinner size="lg" /></div>
  );

  const email = (me as { email?: string })?.email ?? '';
  const initials = firstName?.[0] || email[0] || '?';

  return (
    <div className="page" style={{ maxWidth: 560 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 32 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14,
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, fontWeight: 700, color: '#fff', flexShrink: 0,
        }}>
          {initials.toUpperCase()}
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#e2e8f0' }}>My Account</h1>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: '#475569' }}>Super Admin</p>
        </div>
      </div>

      {/* Email — read only */}
      <Section icon={<Mail size={15} />} title="Email">
        <div style={{
          padding: '10px 14px', background: '#0a0e1c', border: '1px solid #1a1f35',
          borderRadius: 9, fontSize: 13, color: '#64748b', fontFamily: 'monospace',
        }}>
          {email}
        </div>
        <p style={{ margin: '5px 0 0', fontSize: 11, color: '#334155' }}>
          Email is managed by Firebase — change it through Firebase Console.
        </p>
      </Section>

      {/* Name */}
      <Section icon={<User size={15} />} title="Name">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>First name</label>
            <input
              id="account-first-name"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              placeholder="Henri"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Last name</label>
            <input
              id="account-last-name"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              placeholder="Hovi"
              style={inputStyle}
            />
          </div>
        </div>
      </Section>

      {/* Alerts */}
      <Section icon={<Bell size={15} />} title="Notifications">
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
          <input
            id="account-receive-alerts"
            type="checkbox"
            checked={receiveAlerts}
            onChange={e => setReceiveAlerts(e.target.checked)}
            style={{ width: 16, height: 16, marginTop: 1, accentColor: 'var(--color-primary)', cursor: 'pointer', flexShrink: 0 }}
          />
          <div>
            <p style={{ margin: 0, fontSize: 13, color: '#e2e8f0', fontWeight: 600 }}>Receive platform alerts</p>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#475569' }}>
              Get notified about critical platform events, billing, and security.
            </p>
          </div>
        </label>
      </Section>

      {/* Role info — read only */}
      <Section icon={<Shield size={15} />} title="Role">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: '#6366f120', color: '#818cf8', border: '1px solid #6366f140',
            letterSpacing: '0.05em',
          }}>
            SUPER ADMIN
          </span>
          <span style={{ fontSize: 12, color: '#475569' }}>Full platform access — cannot be changed.</span>
        </div>
      </Section>

      {/* Save */}
      <button
        id="account-save-btn"
        onClick={() => save()}
        disabled={isPending}
        style={{
          marginTop: 8, padding: '10px 24px',
          background: 'var(--color-primary)', border: 'none',
          borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 600,
          cursor: isPending ? 'not-allowed' : 'pointer',
          opacity: isPending ? 0.7 : 1,
          display: 'flex', alignItems: 'center', gap: 8,
        }}
      >
        {isPending ? <Spinner size="sm" /> : null}
        {isPending ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
        <span style={{ color: '#6366f1' }}>{icon}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', marginBottom: 5,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#0a0e1c',
  border: '1px solid #1a1f35', borderRadius: 8, color: '#e2e8f0',
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
